/**
 * /garage/inscription — demande d'accès d'un garage partenaire (2026-09-17).
 *
 * Deux barrières, et la page le dit franchement : le SIRET est vérifié
 * automatiquement auprès de l'annuaire des entreprises, puis la demande est
 * validée À LA MAIN. C'est cette seconde étape qui protège les vendeurs, donc
 * on ne la présente pas comme une formalité.
 *
 * Le SIRET est vérifié AVANT l'envoi : le garage voit la raison sociale et
 * l'adresse que nous avons trouvées, et corrige tout de suite si ce n'est pas
 * son établissement.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import api from '../services/api';
import './GarageInscriptionPage.css';

const RAYONS = [25, 50, 100, 200];
const VIDE = { siret: '', email: '', mot_de_passe: '', telephone: '', rayon_km: 50 };

const message = (err, repli) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, '');
  return repli;
};

export default function GarageInscriptionPage() {
  const [champs, setChamps] = useState(VIDE);
  const [etablissement, setEtablissement] = useState(null);
  const [cgu, setCgu] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [envoye, setEnvoye] = useState(false);

  const maj = (k) => (e) => setChamps((c) => ({ ...c, [k]: e.target.value }));

  async function verifierSiret() {
    const siret = champs.siret.replace(/\D/g, '');
    if (siret.length !== 14) { setEtablissement(null); return; }
    setOccupe(true);
    setErreur(null);
    try {
      const { data } = await api.post('/api/garage/verifier-siret', { siret });
      setEtablissement(data);
    } catch (err) {
      setEtablissement(null);
      setErreur(message(err, 'Ce SIRET n’a pas pu être vérifié.'));
    } finally {
      setOccupe(false);
    }
  }

  async function envoyer(e) {
    e.preventDefault();
    setOccupe(true);
    setErreur(null);
    try {
      await api.post('/api/garage/inscription', {
        siret: champs.siret.replace(/\D/g, ''),
        email: champs.email.trim(),
        mot_de_passe: champs.mot_de_passe,
        telephone: champs.telephone.trim() || null,
        rayon_km: Number(champs.rayon_km),
        cgu_garage_acceptees: cgu,
      });
      setEnvoye(true);
    } catch (err) {
      setErreur(message(err, 'Votre demande n’a pas pu être envoyée.'));
    } finally {
      setOccupe(false);
    }
  }

  if (envoye) {
    return (
      <Page largeur="etroite" titre="Demande envoyée">
        <section className="zk-carte zk-gin-confirmation">
          <h2>Nous vérifions votre demande</h2>
          <p>
            Votre SIRET est vérifié. Chaque garage est ensuite validé à la main avant d’accéder aux véhicules à vendre :
            c’est ce qui permet de promettre aux vendeurs que les offres reçues sont sérieuses.
          </p>
          <p className="zk-gin-muet">Vous recevrez un email dès que votre accès sera ouvert.</p>
          <Link to="/" className="zk-bouton">Retour à l’accueil</Link>
        </section>
      </Page>
    );
  }

  return (
    <Page
      largeur="etroite"
      titre="Accès garage partenaire"
      chapeau="Recevez les véhicules que des particuliers vendent autour de vous, avant qu’ils ne partent ailleurs."
    >
      <details className="zk-gin-pourquoi">
        <summary>comment ça marche ?</summary>
        <p>
          Les particuliers déposent leur véhicule sur ZENKART et obtiennent une estimation honnête. Vous voyez leur fiche
          <strong> anonyme</strong> — véhicule, kilométrage, état, photos, secteur — et vous déverrouillez les coordonnées
          des vendeurs qui vous intéressent. Le vendeur est prévenu que vous l’avez demandé.
        </p>
        <p>
          Les coordonnées servent à faire une offre sur ce véhicule, et à rien d’autre :
          {' '}<Link to="/cgu-garage" className="zk-lien">voir les conditions</Link>.
        </p>
      </details>

      {erreur && <div role="alert" className="zk-alerte erreur zk-gin-bloc">{erreur}</div>}

      <form className="zk-carte zk-gin-form" onSubmit={envoyer}>
        <div className="zk-champ">
          <label htmlFor="gin-siret">SIRET de l’établissement</label>
          <input
            id="gin-siret" required inputMode="numeric" maxLength={20} placeholder="312 212 301 02001"
            value={champs.siret} onChange={maj('siret')} onBlur={verifierSiret}
          />
          <small>14 chiffres. Nous le vérifions auprès de l’annuaire des entreprises.</small>
        </div>

        {etablissement && (
          <div className="zk-gin-etablissement">
            <b>{etablissement.raison_sociale}</b>
            <span>{etablissement.adresse}</span>
            <small>Ce n’est pas votre établissement ? Corrigez le SIRET ci-dessus.</small>
          </div>
        )}

        <div className="zk-gin-grille">
          <div className="zk-champ">
            <label htmlFor="gin-email">Email professionnel</label>
            <input id="gin-email" required type="email" autoComplete="email" value={champs.email} onChange={maj('email')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="gin-tel">Téléphone</label>
            <input id="gin-tel" type="tel" autoComplete="tel" placeholder="05 56 12 34 56"
              value={champs.telephone} onChange={maj('telephone')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="gin-mdp">Mot de passe</label>
            <input id="gin-mdp" required type="password" autoComplete="new-password" minLength={12}
              value={champs.mot_de_passe} onChange={maj('mot_de_passe')} />
            <small>12 caractères minimum.</small>
          </div>
          <div className="zk-champ">
            <label htmlFor="gin-rayon">Rayon de prospection</label>
            <select id="gin-rayon" value={champs.rayon_km} onChange={maj('rayon_km')}>
              {RAYONS.map((r) => <option key={r} value={r}>{r} km autour de l’établissement</option>)}
            </select>
          </div>
        </div>

        <label className="zk-gin-cgu">
          <input type="checkbox" checked={cgu} onChange={(e) => setCgu(e.target.checked)} required />
          <span>
            J’accepte les <Link to="/cgu-garage" target="_blank" rel="noopener noreferrer">conditions garages partenaires</Link>,
            et notamment l’interdiction de réutiliser, revendre ou céder les coordonnées d’un vendeur, ou de le démarcher
            au-delà de l’offre de rachat concernée.
          </span>
        </label>

        <button type="submit" className="zk-bouton plein" disabled={occupe || !cgu}>
          {occupe ? 'Vérification…' : 'Demander l’accès'}
        </button>
        <p className="zk-gin-muet">
          Votre demande est examinée à la main. Aucun prélèvement n’est effectué à l’inscription.
        </p>
      </form>
    </Page>
  );
}
